import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, MessageSquare, X } from 'lucide-react';

import { useLingui } from '@lingui/react/macro';

/**
 * Confirm Dialog thay thế confirm() trình duyệt.
 *
 * Usage:
 *   const [confirm, ConfirmUI] = useConfirm();
 *   const ok = await confirm({ title: 'Xác nhận?', message: '...' });
 *   if (ok) { ... }
 *   return <>{ConfirmUI}</>
 */
export function useConfirm() {
  const {
    t
  } = useLingui();

  const [state, setState] = useState(null); // { title, message, confirmText, cancelText, variant, resolve }

  const confirm = (opts = {}) => new Promise(resolve => {
    setState({ ...opts, resolve });
  });

  const handleConfirm = () => { state?.resolve(true); setState(null); };
  const handleCancel = () => { state?.resolve(false); setState(null); };

  // TC-111: Esc đóng modal (đã có click outside via overlay onClick) — port từ Core.
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') handleCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Portal + z-[10000] để confirm modal LUÔN nằm trên SlideDrawer portals (z-9999) +
  // các overlay khác. Trước đây inline z-[60] — trên trang có nhiều DrawerProvider
  // stacked (vd /approvals/templates: Task/Approval/7 CRM/Ticket/Fulfillment Drawer
  // providers), DrawerProvider's outer `fixed inset-0 z-[9999]` div (dù invisible khi
  // closed) tạo stacking context cao hơn → modal z-60 bị che kín visually.
  const UI = state ? ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={handleCancel}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${state.variant === 'danger' ? 'bg-red-100' : 'bg-amber-100'}`}>
              <AlertTriangle size={20} className={state.variant === 'danger' ? 'text-red-600' : 'text-amber-600'} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900">{state.title || t`Xác nhận`}</h3>
              {state.message && <p className="text-sm text-gray-500 mt-1">{state.message}</p>}
            </div>
            <button onClick={handleCancel} className="p-1 hover:bg-gray-100 rounded-lg shrink-0">
              <X size={18} className="text-gray-400" />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={handleCancel}
            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
            {state.cancelText || t`Huỷ`}
          </button>
          <button onClick={handleConfirm}
            className={`px-4 py-2.5 rounded-xl text-sm font-medium text-white transition ${state.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {state.confirmText || t`Xác nhận`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return [confirm, UI];
}

/**
 * Prompt Dialog thay thế window.prompt(). Trả về string user nhập hoặc null khi cancel.
 *
 * Usage:
 *   const [prompt, PromptUI] = usePrompt();
 *   const value = await prompt({ title: 'Nhập URL', defaultValue: 'https://' });
 *   if (value !== null) { ... }
 *   return <>{PromptUI}</>
 */
export function usePrompt() {
  const {
    t
  } = useLingui();

  const [state, setState] = useState(null);
  const [value, setValue] = useState('');

  const prompt = (opts = {}) => new Promise(resolve => {
    setValue(opts.defaultValue ?? '');
    setState({ ...opts, resolve });
  });

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    state?.resolve(value);
    setState(null);
    setValue('');
  };
  const handleCancel = () => {
    state?.resolve(null);
    setState(null);
    setValue('');
  };

  const UI = state ? ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={handleCancel}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-blue-100">
                <MessageSquare size={20} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-gray-900">{state.title || t`Nhập thông tin`}</h3>
                {state.message && <p className="text-sm text-gray-500 mt-1">{state.message}</p>}
              </div>
              <button type="button" onClick={handleCancel} className="p-1 hover:bg-gray-100 rounded-lg shrink-0">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            <div className="mt-4">
              {state.multiline ? (
                <textarea
                  autoFocus
                  rows={4}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={state.placeholder || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
              ) : (
                <input
                  type={state.inputType || 'text'}
                  autoFocus
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={state.placeholder || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button type="button" onClick={handleCancel}
              className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
              {state.cancelText || t`Huỷ`}
            </button>
            <button type="submit"
              disabled={state.required && !value.trim()}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition">
              {state.confirmText || t`Xác nhận`}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  ) : null;

  return [prompt, UI];
}
